import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signup, login, verifyOtp, resendOtp, clearAuthError } from "../../store/auth/authSlice";
import { EyeIcon, EyeOffIcon } from "./PasswordEyeIcons";
import { useToast } from "../ToastProvider";
import { isAuthErrorMessage } from "../../api/client";

export default function AuthOverlay({
  open,
  initialMode = "login",
  onClose,
  onLoginSuccess,
  onOpenForgotPassword,
}) {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const authStatus = useAppSelector((s) => s.auth.status);
  const authError = useAppSelector((s) => s.auth.error);
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("form"); // form | otp | success
  const [otp, setOtp] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const emptyForm = useMemo(
    () => ({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    }),
    []
  );
  const [form, setForm] = useState(emptyForm);
  const [formKey, setFormKey] = useState(0);

  const resetFormFields = useCallback(() => {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFormKey((k) => k + 1);
  }, [emptyForm]);

  const enableFieldEdit = useCallback((e) => {
    e.target.removeAttribute("readonly");
  }, []);

  useEffect(() => {
    if (!open) return;
    dispatch(clearAuthError());
    setMode(initialMode);
    setStep("form");
    setOtp("");
    setOtpError("");
    setOtpInfo("");
    resetFormFields();
  }, [open, initialMode, resetFormFields, dispatch]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const visibleAuthError =
    authError && !isAuthErrorMessage(authError) ? authError : "";

  if (!open) return null;

  const isLogin = mode === "login";
  const primaryLabel =
    step === "form" ? (isLogin ? "Login" : "Sign Up") : step === "otp" ? "Validate OTP" : "Go to Home";

  async function submitForm(e) {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});

    const nextErrors = {};

    // Common email/password validation
    if (!form.email?.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!form.password) nextErrors.password = "Password is required.";

    // Extra checks for signup
    if (!isLogin) {
      if (!form.name?.trim()) {
        nextErrors.name = "Full Name is required.";
      }
      if (!form.confirmPassword) {
        nextErrors.confirmPassword = "Confirm Password is required.";
      } else if (form.password && form.password !== form.confirmPassword) {
        nextErrors.confirmPassword = "Passwords do not match.";
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      if (isLogin) {
        const data = await dispatch(
          login({
            email: form.email,
            password: form.password,
          })
        ).unwrap();
        showToast({
          type: "success",
          title: "Login",
          message: data?.message || "Login successful",
        });
        onLoginSuccess?.();
      } else {
        await dispatch(
          signup({
            full_name: form.name,
            email: form.email,
            password: form.password,
            password_confirmation: form.confirmPassword,
          })
        ).unwrap();
        showToast({
          type: "success",
          title: "Signup",
          message: "Signup successful. Please verify OTP.",
        });
        setStep("otp");
      }
    } catch (err) {
      setFormError(typeof err === "string" ? err : "Signup failed.");
    }
  }

  function submitOtp(e) {
    e.preventDefault();
    setOtpError("");

    const cleaned = (otp || "").replace(/\D/g, "");
    if (cleaned.length !== 4) {
      setOtpError("Please enter the 4 digit OTP.");
      return;
    }

    dispatch(verifyOtp({ otp: cleaned }))
      .unwrap()
      .then(() => {
        showToast({
          type: "success",
          title: "OTP Verified",
          message: "Your account has been verified successfully.",
        });
        setStep("success");
      })
      .catch((err) => {
        setOtpError(typeof err === "string" ? err : "Invalid OTP. Please try again.");
      });
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close authentication modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white shadow-xl">
          {step === "success" ? null : step === "otp" ? (
            <div className="relative px-6 pb-2 pt-6">
              <button
                type="button"
                onClick={() => {
                  setStep("form");
                  setOtp("");
                }}
                className="absolute left-6 top-6 rounded-full p-2 text-slate-700 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                aria-label="Back"
              >
                ←
              </button>
              <p className="text-center text-[24px] font-semibold tracking-tight text-femty-text md:text-[28px]">
                Validate OTP
              </p>
            </div>
          ) : (
            <div className="relative flex items-center justify-center px-6 pt-5">
              <p
                className={`text-[#24303A] font-bold tracking-tight ${
                  step === "form" ? "text-[24px] md:text-[28px]" : "text-[18px] md:text-[20px]"
                }`}
              >
                {step === "form" ? (isLogin ? "Sign In" : "Sign Up") : "Verified"}
              </p>

              <button
                type="button"
                onClick={onClose}
                className="absolute right-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E6EAF0] bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          )}

          <div className="px-6 pb-6 pt-2">
            {step === "success" ? (
              <div className="flex flex-col items-center text-center pb-4 pt-8">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-femty-savings-green">
                  <span className="text-3xl text-femty-savings-green">✔</span>
                </div>
                <h3 className="text-[18px] font-semibold text-femty-text md:text-[20px]">Congratulations</h3>
                <p className="mt-2 text-[16px] font-normal text-femty-muted">Your account has been verified.</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-8 w-full rounded-full bg-femty-primary py-1.5 text-[16px] font-medium text-white "
                >
                  Home 
                </button>
              </div>
            ) : step === "otp" ? (
              <form onSubmit={submitOtp} className="space-y-6">
                <div className="text-center">
                  <p className="text-[16px] font-normal text-femty-muted">
                    Enter 4 digit OTP sent to Email
                  </p>
                  {otpInfo && <p className="mt-2 text-[14px] font-medium text-femty-muted">{otpInfo}</p>}
                </div>

                <div>
                  <input
                    value={otp}
                    onChange={(e) => setOtp((e.target.value || "").replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    placeholder="OTP"
                    className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-1.5 text-center text-[16px] font-normal text-[#6E7B86] shadow-sm focus:border-femty-primary focus:outline-none"
                  />
                  {otpError && <p className="mt-1 text-[16px] text-red-600">{otpError}</p>}
                </div>

                <button
                  type="submit"
                  className="w-full rounded-full bg-femty-primary py-1.5 text-[16px] font-bold text-white disabled:cursor-not-allowed "
                  disabled={authStatus === "loading"}
                >
                  {authStatus === "loading" ? "Please wait..." : "Verify"}
                </button>

                <button
                  type="button"
                  className="mx-auto block text-[16px] font-medium text-[#7352C7] disabled:cursor-not-allowed"
                  disabled={authStatus === "loading"}
                  onClick={() => {
                    setOtpError("");
                    setOtpInfo("");
                    dispatch(resendOtp())
                      .unwrap()
                      .then((data) => {
                        const message = data?.message || "OTP resent successfully.";
                        setOtpInfo(message);
                        showToast({ type: "success", title: "OTP Sent", message });
                      })
                      .catch((err) => {
                        const message =
                          typeof err === "string" ? err : "Failed to resend OTP. Please try again.";
                        setOtpError(message);
                        showToast({ type: "error", title: "OTP Error", message });
                      });
                  }}
                >
                  Resend OTP
                </button>
              </form>
            ) : (
              <form
                key={formKey}
                onSubmit={submitForm}
                className="space-y-4"
                autoComplete={isLogin ? "on" : "off"}
              >
                {!!(formError || visibleAuthError) && (
                  <div className="whitespace-pre-line rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[16px] text-red-700">
                    {formError || visibleAuthError}
                  </div>
                )}

                {!isLogin && (
                  <div>
                    <input
                      value={form.name}
                      onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                      onFocus={enableFieldEdit}
                      readOnly
                      name="signup-full-name"
                      autoComplete="name"
                      className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                      placeholder="Full Name"
                    />
                    {fieldErrors.name && <p className="mt-1 text-[16px] text-red-600">{fieldErrors.name}</p>}
                  </div>
                )}

                <div>
                  <input
                    value={form.email}
                    onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                    onFocus={enableFieldEdit}
                    readOnly
                    name={isLogin ? "login-email" : "signup-email"}
                    autoComplete={isLogin ? "username" : "email"}
                    className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                    placeholder="Email"
                    type="email"
                  />
                  {fieldErrors.email && <p className="mt-1 text-[16px] text-red-600">{fieldErrors.email}</p>}
                </div>

                <div>
                  <div className="relative">
                    <input
                      value={form.password}
                      onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
                      onFocus={enableFieldEdit}
                      readOnly
                      name={isLogin ? "login-password" : "signup-password"}
                      autoComplete={isLogin ? "current-password" : "new-password"}
                      className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 pr-10 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                      placeholder="Password"
                      type={showPassword ? "text" : "password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-femty-primary"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="mt-1 text-[16px] text-red-600">{fieldErrors.password}</p>}
                </div>

                {!isLogin && (
                  <div>
                    <div className="relative">
                      <input
                        value={form.confirmPassword}
                        onChange={(e) => setForm((s) => ({ ...s, confirmPassword: e.target.value }))}
                        onFocus={enableFieldEdit}
                        readOnly
                        name="signup-password-confirmation"
                        autoComplete="new-password"
                        className="w-full rounded-full border border-[#E6EAF0] bg-white px-4 py-2 pr-10 text-[16px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                        placeholder="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-femty-primary"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    {fieldErrors.confirmPassword && (
                      <p className="mt-1 text-[16px] text-red-600">{fieldErrors.confirmPassword}</p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full rounded-full bg-[#7352C7] py-2 text-[16px] font-medium text-white disabled:cursor-not-allowed"
                  disabled={authStatus === "loading"}
                >
                  {authStatus === "loading" ? "Please wait..." : `${primaryLabel}`}
                </button>

                <div className="flex justify-center gap-16">
                  {isLogin && (
                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => {
                          onClose?.();
                          onOpenForgotPassword?.();
                        }}
                        className="text-[14px] font-medium text-[#7352C7] hover:text-femty-primary"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  <div className="text-center text-[14px] font-normal text-[#6E7B86] self-center">
                    {isLogin ? (
                      <>
                        Don’t have an account?{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setMode("signup");
                            resetFormFields();
                          }}
                          className="font-medium text-[#7352C7]"
                        >
                          Sign up
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="text-center text-[14px] font-normal text-[#6E7B86] self-center">
                        Already have an account?{" "}
                        <button
                          type="button"
                          onClick={() => {
                            setMode("login");
                            resetFormFields();
                          }}
                          className="font-medium text-[#7352C7] text-[14px]"
                        >
                          Sign in now
                        </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
